/* eslint-disable react/no-danger */
import './styles.scss';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import track from 'react-tracking';
import { withPrefix } from 'gatsby';

import { Collapsible } from '@edx/paragon';
import { School, Compass, LocationOn } from '@edx/paragon/icons';
import { defineMessages, FormattedMessage, injectIntl } from 'gatsby-plugin-react-intl';

import { intlShape } from '@prospectus/common/types/intlShape';
import { appendProgramToProgramType } from '@utils/edxProductDisplay';
import {
  ListStyles, formatList, formatSelectKey,
} from '@utils/formatting';
import { submitEvent } from '@utils/analytics';
import { checkRTL } from '@utils/rtlLanguageHoc';
import CloudflareImage from '@prospectus/common/ui/CloudflareImage';
import ProgramPathwayCourse from './components/ProgramPathwayCourse';

const messages = defineMessages({
  'prospectus.program_pathway.job_outlook': {
    id: 'prospectus.program_pathway.job_outlook',
    description: 'Title for section describing career prospects',
    defaultMessage: 'Job Outlook',
  },
  'prospectus.program_pathway.owner_title_type': {
    id: 'prospectus.program_pathway.owner_title_type',
    description: 'Title describing whose program this is and its type',
    defaultMessage: '{ownersList}\'s {title} {programType}',
  },
  'prospectus.program_pathway.owner_title_type.possessive_alt': {
    id: 'prospectus.program_pathway.owner_title_type.possessive_alt',
    description: 'Title describing whose program this is and its type',
    defaultMessage: '{ownersList}\' {title} {programType}',
  },
});

function ProgramPathway({
  title,
  type,
  activeCourse,
  programCourses,
  credentialInfo,
  jobOutlook,
  owners,
  isBundled,
  intl,
  linkedProgramCourses,
  tracking,
}) {
  const [activeCourses, setActiveCourses] = useState([]);
  const [electivesChecked, setElectivesChecked] = useState(false);
  const [electiveCourses, setElectiveCourses] = useState([]);
  const [requiredCourses, setRequiredCourses] = useState([]);

  const handleTogglePathway = (isOpen, label, uuid = '') => {
    const componentProps = {
      category: 'navigation',
      label,
      isOpen,
    };

    submitEvent(tracking, componentProps, 'edx.bi.interactive-course-pathway.clicked');

    if (isOpen) {
      setActiveCourses([...activeCourses, uuid]);
    } else {
      const filteredCourses = activeCourses.filter(course => course !== uuid);
      setActiveCourses(filteredCourses);
    }
  };

  const getLastStep = () => {
    if (jobOutlook.length > 0) {
      return 'job-outlook';
    }

    if (credentialInfo && credentialInfo.length > 0) {
      return 'credential-info';
    }

    return 'courses';
  };

  /**
   * Professional Certificates/Xseries don't have credentials, but they put random program
   * information in the credentials section, so we just call it `Details`
  */
  const getCertificateLabel = () => (
    <FormattedMessage
      id="prospectus.program_pathway.type_details"
      description="Introduce details of this program type"
      defaultMessage={'{typeKey, select, '
          + 'micromasters {Certificate & Credit Pathways} '
          + 'professional_certificate {{type} Details} '
          + 'xseries {{type} Details} '
          + 'microbachelors {Program Certificate & College Credit}'
          + 'other {{type} Certificate}}'}
      values={{ type, typeKey: formatSelectKey(type) }}
    />
  );
  const courseIsActive = (uuid) => activeCourses.indexOf(uuid) > -1;

  // For program track selection. Compare the course lists from the
  // program and linked program to find which are "electives" and which are
  // required. Save these in state.
  const findElectives = () => {
    const programCourseUuids = programCourses.map(course => course.uuid);
    const linkedprogramCourseUuids = linkedProgramCourses.map(course => course.uuid);

    // The required courses are all the courses in the program that are also in
    // the linked program.
    const foundRequiredCourses = programCourses.filter(course => linkedprogramCourseUuids.includes(course.uuid));

    // The elective courses are all the courses in the program that are not in
    // the linked program, and all the courses in the linked program that are
    // are not in the program (there should only be one of each).
    const uniqueProgramCourses = programCourses.filter(course => !linkedprogramCourseUuids.includes(course.uuid));
    const uniqueLinkedProgramCourses = linkedProgramCourses.filter(course => !programCourseUuids.includes(course.uuid));
    const foundElectiveCourses = uniqueProgramCourses.concat(uniqueLinkedProgramCourses);

    setElectivesChecked(true);
    setElectiveCourses(foundElectiveCourses);
    setRequiredCourses(foundRequiredCourses);
  };

  const renderCourses = (courseArray, lastStep, courseLength) => courseArray.map(({
    activeCourseRun: courseRun,
    title: courseTitle,
    subtitle,
    uuid: courseUuid,
    prospectusPath,
  }, index) => (
    <ProgramPathwayCourse
      key={courseTitle}
      courseTitle={courseTitle}
      subtitle={subtitle}
      courseUuid={courseUuid}
      prospectusPath={prospectusPath}
      courseRun={courseRun}
      courseIsActive={courseIsActive(courseUuid)}
      lastStep={lastStep}
      courseLength={courseLength}
      activeCourse={activeCourse}
      intl={intl}
      isBundled={isBundled}
      handleTogglePathway={handleTogglePathway}
      index={index}
    />
  ));

  const courseLength = programCourses.length;
  const finalCertificate = getCertificateLabel();
  const lastStep = getLastStep();
  /*
      Removed for rebrand until design can be revisited
      Implementation ticket: MICROBA-834
      Fix ticket: MICROBA-836
    */
  // const showProgramLogo = type.toLowerCase() === 'micromasters';
  const showProgramLogo = false;

  // For program track selection. If this program has a linked program,
  // we need to sort out the courses (if we haven’t already)
  if (linkedProgramCourses && !electivesChecked) { findElectives(); }
  const courses = (electivesChecked ? requiredCourses : programCourses).filter((course) => course !== null);
  const ownersList = formatList(owners.map(owner => owner.key), ListStyles.conjunction, intl);
  const useAltPossessiveFormat = ownersList.toLowerCase().endsWith('s');
  const programType = appendProgramToProgramType(type);

  const programPathwayTitle = intl.formatMessage(
    messages[`prospectus.program_pathway.owner_title_type${useAltPossessiveFormat ? '.possessive_alt' : ''}`],
    { ownersList, title, programType },
  );

  const isJobOutlookRtl = checkRTL(jobOutlook.join(''));

  return (
    <div className="program-pathway-component interactive">
      <ol className="pathway">
        <li
          key="program-title"
          className="step title"
        >
          <div className="path">
            <div className="marker">
              <LocationOn style={{ transform: 'scale(1.5)' }} className="path-icon" />
            </div>
          </div>
          <div className="path-details d-flex flex-row">
            <div className={classNames({ 'col-lg-8': showProgramLogo })}>
              <h2 className="program-pathway-title">
                {programPathwayTitle}
              </h2>
            </div>
            {showProgramLogo
                && (
                <div className="program-logo-wrapper col-lg-4">
                  <CloudflareImage
                    url={withPrefix('/images/program/mm-logo-blue.png')}
                    className="program-logo"
                  />
                </div>
                )}
          </div>
        </li>
        {linkedProgramCourses
            && requiredCourses.length > 0
            && (
            <li className="step course-group-heading">
              <div className="path">
                <div className="marker" />
              </div>
              <div className="path-details">
                <h3 className="program-pathway-course-group-heading">
                  <FormattedMessage
                    id="prospectus.program_pathway.course-group-heading-complete-all"
                    description="Heading indicating courses to be completed"
                    defaultMessage="Complete all:"
                  />
                </h3>
              </div>
            </li>
            )}
        {renderCourses(courses, lastStep, courseLength)}
        {linkedProgramCourses
            && electiveCourses.length > 0
            && (
            <li className="step course-group-heading">
              <div className="path">
                <div className="marker" />
              </div>
              <div className="path-details">
                <h3 className="program-pathway-course-group-heading">
                  <FormattedMessage
                    id="prospectus.program_pathway.course-group-heading-select-one"
                    description="Heading indicating a choice between two courses"
                    defaultMessage="Select one:"
                  />
                </h3>
              </div>
            </li>
            )}
        {linkedProgramCourses
            && electiveCourses.length > 0
            && renderCourses(electiveCourses, lastStep, courseLength)}
        {linkedProgramCourses
            && (
            <li className="step course-group-heading py-4">
              <div className="path">
                <div className="marker" />
              </div>
            </li>
            )}
        {credentialInfo && credentialInfo.length > 0
            && (
            <li
              key="credential-info"
              className={classNames('step', 'outcome', { last: lastStep === 'credential-info' })}
            >
              <div className="path">
                <div className="marker">
                  <School className="path-icon" />
                </div>
              </div>
              <div className="path-details">
                <Collapsible className="pl-5.5 py-3.5 pb-3.5" title={finalCertificate}>
                  <div className="program-credential font-weight-normal mt-2" dangerouslySetInnerHTML={{ __html: credentialInfo }} />
                </Collapsible>
              </div>
            </li>
            )}
        {jobOutlook && jobOutlook.length > 0
            && (
            <li
              key="job-outlook"
              className={classNames('step', 'outcome', { last: lastStep === 'job-outlook' })}
            >
              <div className="path">
                <div className="marker">
                  <Compass className="path-icon" />
                </div>
              </div>
              <div className="path-details">
                <Collapsible
                  className="pl-5.5 py-3.5 pb-3.5"
                  title={intl.formatMessage(messages['prospectus.program_pathway.job_outlook'])}
                  onToggle={isOpen => handleTogglePathway(isOpen, intl.formatMessage(messages['prospectus.program_pathway.job_outlook']))}
                >
                  <ul className={`overview-info font-weight-normal mt-2 pl-3 ${isJobOutlookRtl ? 'rtl' : ''}`}>
                    {jobOutlook.map(paragraph => <li key={paragraph}>{paragraph}</li>)}
                  </ul>
                </Collapsible>
              </div>
            </li>
            )}
      </ol>
    </div>
  );
}

ProgramPathway.propTypes = {
  title: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  programCourses: PropTypes.arrayOf(PropTypes.shape({
    uuid: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string.isRequired,
    activeCourseRun: PropTypes.shape({
      key: PropTypes.string.isRequired,
      uuid: PropTypes.string.isRequired,
      marketingPath: PropTypes.string.isRequired,
      minEffort: PropTypes.number.isRequired,
      maxEffort: PropTypes.number.isRequired,
      weeksToComplete: PropTypes.number.isRequired,
    }),
    prospectusPath: PropTypes.string,
  })).isRequired,
  linkedProgramCourses: PropTypes.arrayOf(PropTypes.shape({
    uuid: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string.isRequired,
    activeCourseRun: PropTypes.shape({
      key: PropTypes.string.isRequired,
      uuid: PropTypes.string.isRequired,
      marketingPath: PropTypes.string.isRequired,
      minEffort: PropTypes.number.isRequired,
      maxEffort: PropTypes.number.isRequired,
      weeksToComplete: PropTypes.number.isRequired,
    }),
    prospectusPath: PropTypes.string,
  })),
  owners: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    key: PropTypes.string,
    logoImageUrl: PropTypes.string,
  })).isRequired,
  activeCourse: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool,
  ]),
  credentialInfo: PropTypes.string,
  jobOutlook: PropTypes.arrayOf(PropTypes.string).isRequired,
  isBundled: PropTypes.bool.isRequired,
  intl: intlShape.isRequired,
  tracking: PropTypes.shape(),
};

ProgramPathway.defaultProps = {
  activeCourse: false,
  credentialInfo: '',
  tracking: {},
  linkedProgramCourses: null,
};

export default track({
  component: 'Interactive-course-pathway-to-program',
})(injectIntl(ProgramPathway));
